<template>
    <div class="page-gutter">
        <div v-if="isLoading" style="display: flex; justify-content: center;">
            <div class="loader" style="margin-top: 400px;"></div>
        </div>

        <div v-if="!isLoading">
            <!-- Unified section head with a right-aligned action -->
            <Section :title="$t('explore.home.title', { store: CompanyStore.company.CompanyName })">
                <!-- Ask Allen now lives on the Home page (HomeOverview), beneath
                     Allen's read — the old per-page chatbot button is retired. -->

                <!-- Content -->
                <fixed-widgets
                :fixed-widgets-data="fixedWidgetData"
                :fixed-widgets-compare-to-data="fixedWidgetCompareToData"
                :fixed-widgets-data30d="fixedWidgetData30d"
                @selectedDuration="handleUpdateDuration"
                @selectedOpeningHoursMode="handleUpdateOpeningHoursMode"
                />
            </Section>
        </div>
    </div>
</template>
  
<script>
import { ref, onMounted, onUnmounted, watch, defineComponent, nextTick } from 'vue'

import { useUserStore } from '@/stores/UserStore'
import { useCompanyStore } from '@/stores/CompanyStore'

import Section from '@/components/ui/Section.vue'
import FixedWidgets from './FixedWidgets.vue'
import CurrentLiveVisitorsData from './CurrentLiveVisitorsData.vue'
import CurrentLiveOpportunityData from './CurrentLiveOpportunityData.vue'

import {getFixedDashboardCountersValues, getZoneTypeAnalytics} from "@/api/eventsAPI.js"

export default defineComponent({
    name: 'Dashboard',

    components: {
        Section,
        FixedWidgets,
        CurrentLiveVisitorsData,
        CurrentLiveOpportunityData,

    },

    setup() {
        const CompanyStore = useCompanyStore()
        const userStore = useUserStore()
        const controller = new AbortController()

        const selectedDurationFixedWidgetData = ref(30)
        const selectedOpeningHoursModeFixedWidgetData = ref('only_regular_opening_hours')
        const dateRangeMode = ref('duration')
        const clearDatePicker = ref(null)
        const dataGranularity = ref('daily')
        const fixedWidgetData = ref({})
        const fixedWidgetCompareToData = ref({})
        // A stable 30-day snapshot for Actions (its suggestions must NOT move when
        // the picker slices Zones/Explore). Captured whenever a 30d fetch lands
        // (mount + any return to 30d); retained across non-30d picker changes.
        const fixedWidgetData30d = ref({})
        const isLoading = ref(true)

        onMounted(async () => {
            await userStore.fetchUserDetails()
            if (userStore.currentCompany.CompanyId) {
                await CompanyStore.fetchCompanyDetails(userStore.currentCompany.CompanyId)
            }
            await fetchFixedWidgetData()
            isLoading.value = false
        })

        watch(() => dateRangeMode.value, async (newValue) => {
            if (newValue) {
                try {
                    await fetchFixedWidgetData()
                } catch (error) {
                    console.error('Error updating widgets:', error)
                }
            }
        })

        watch(() => dataGranularity.value, async (newValue) => {
            if (newValue) {
                try {
                    await fetchFixedWidgetData()
                } catch (error) {
                    console.error('Error updating widgets:', error)
                }
            }
        })

        const fetchFixedWidgetData = async () => {
            try {
                
                // 1) Fixed widget counters
                const fullData = await getFixedDashboardCountersValues(
                selectedDurationFixedWidgetData.value,
                selectedOpeningHoursModeFixedWidgetData.value,
                { signal: controller.signal }
                )

                // 2) Aggregated zone-type analytics for interaction (5) + cashier (999)
                const zoneTypeData = await getZoneTypeAnalytics(
                selectedDurationFixedWidgetData.value,
                selectedOpeningHoursModeFixedWidgetData.value,
                [5, 999],
                { signal: controller.signal }
                )

                const type5 = zoneTypeData?.zone_types?.['5'] || null     // Checkout Interaction Zone aggregate
                const type999 = zoneTypeData?.zone_types?.['999'] || null // Cash Register Zone aggregate

                const newStoredData = {
                    // Pass-through every field the backend produces (so new
                    // additions like the tripwire dwell aggregates and the
                    // Info/Service/Pick Up zone-type fields don't have to be
                    // listed here individually). The few fields that need
                    // local munging are overridden below.
                    ...fullData,

                    // local rounding overrides
                    average_same_weekday: Math.round(fullData.average_same_weekday),
                    average_visitors: Math.round(fullData.average_visitors),
                    average_on_busiest_hour: Math.round(fullData.average_on_busiest_hour),

                    date: fullData.last_updated_date, // Add today's date to track when the data was fetched

                    // type_5 = Checkout Interaction Zone aggregate
                    checkout_interaction_usage_pct: type5?.usage_percentage ?? null,
                    checkout_interaction_total_visitors: type5?.total_visitors ?? null,
                    checkout_interaction_total_time_seconds: type5?.total_time_in_zone_seconds ?? null,
                    checkout_interaction_total_opening_hours: type5?.total_opening_hours ?? null,

                    // type_999 = Cash Register Zone aggregate (cashier presence)
                    checkout_cashier_usage_pct: type999?.usage_percentage ?? null,
                    checkout_cashier_total_visitors: type999?.total_visitors ?? null,
                    checkout_cashier_total_time_seconds: type999?.total_time_in_zone_seconds ?? null,
                    checkout_cashier_total_opening_hours: type999?.total_opening_hours ?? null,

                    // Stamp the slot this data was fetched for, so the €/mo scale
                    // travels WITH the data. FixedWidgets derives periodDays from
                    // this (not the live picker), so on a slot change the scale
                    // can't lead the still-stale data → no inflated €-flash.
                    _selectedDuration: selectedDurationFixedWidgetData.value,
                }

                fixedWidgetData.value = { ...newStoredData }
                // Keep the stable 30-day snapshot for Actions in sync whenever the
                // fetched slot is the 30-day window (mount + any return to it).
                if (selectedDurationFixedWidgetData.value === 30) {
                    fixedWidgetData30d.value = { ...newStoredData }
                }
                console.log(fixedWidgetData.value)

            } catch (error) {
                console.error('Error fetching fixed widget data:', error)
            }

            // ---------------- COMPARE-TO PERIOD ----------------
            // Map the current fixed-window duration to its previous-period
            // sentinel that the API recognises as a compare slot:
            //   30  -> 7000 (compare_to_30_days)
            //   7   -> 6000 (compare_to_7_days)
            //   1   -> 5000 (compare_to_yesterday)
            //   90  -> 8000 (compare_to_90_days)
            // 365 / this_*/last_* don't have compare slots yet.
            let compareToDuration = 0
            if (selectedDurationFixedWidgetData.value == 30){
                compareToDuration = 7000
            } else if (selectedDurationFixedWidgetData.value == 7){
                compareToDuration = 6000
            } else if (selectedDurationFixedWidgetData.value == 1){
                compareToDuration = 5000
            } else if (selectedDurationFixedWidgetData.value == 90){
                compareToDuration = 8000
            }

            if (compareToDuration != 0){
                try {
                    // 1) Fixed widget counters for compare period
                    const fullCompareToData = await getFixedDashboardCountersValues(
                        compareToDuration,
                        selectedOpeningHoursModeFixedWidgetData.value,
                        { signal: controller.signal }
                    )

                    // 2) Aggregated zone types for compare period (same zone types)
                    const zoneTypeCompare = await getZoneTypeAnalytics(
                        compareToDuration,
                        selectedOpeningHoursModeFixedWidgetData.value,
                        [5, 999],
                        { signal: controller.signal }
                    )

                    const type5Cmp = zoneTypeCompare?.zone_types?.['5'] || null
                    const type999Cmp = zoneTypeCompare?.zone_types?.['999'] || null

                    const newStoredCompareToData = {
                        // ---------- existing fixed widget compare fields ----------
                        store_total_visitors: fullCompareToData.store_total_visitors,
                        current_weekday: fullCompareToData.current_weekday,
                        average_same_weekday: Math.round(fullCompareToData.average_same_weekday),
                        average_visitors: Math.round(fullCompareToData.average_visitors),
                        highest_total_hour: fullCompareToData.highest_total_hour,
                        highest_total_hour_count: fullCompareToData.highest_total_hour_count,
                        highest_total_day: fullCompareToData.highest_total_day,
                        highest_total_day_count: fullCompareToData.highest_total_day_count,
                        busiest_hour: fullCompareToData.busiest_hour,
                        average_on_busiest_hour: Math.round(fullCompareToData.average_on_busiest_hour),
                        trend: fullCompareToData.trend,
                        
                        best_product_zone_by_visitors: fullCompareToData.best_product_zone_by_visitors,
                        best_product_zone_by_avg_duration: fullCompareToData.best_product_zone_by_avg_duration,
                        worst_product_zone_by_visitors: fullCompareToData.worst_product_zone_by_visitors,
                        total_duration_per_product_zone: fullCompareToData.total_duration_per_product_zone,
                        percentage_by_duration_range_in_product_zone: fullCompareToData.percentage_by_duration_range_in_product_zone,
                        product_zone_visitors_counts: fullCompareToData.product_zone_visitors_counts,
                        product_zone_clear_lingerers_counts: fullCompareToData.product_zone_clear_lingerers_counts,
                        product_hotcold_by_visitors: fullCompareToData.product_hotcold_by_visitors,
                        product_hotcold_by_clear: fullCompareToData.product_hotcold_by_clear,
                        product_zone_funnels: fullCompareToData.product_zone_funnels,

                        best_demo_zone_by_visitors: fullCompareToData.best_demo_zone_by_visitors,
                        best_demo_zone_by_avg_duration: fullCompareToData.best_demo_zone_by_avg_duration,
                        worst_demo_zone_by_visitors: fullCompareToData.worst_demo_zone_by_visitors,
                        total_duration_per_demo_zone: fullCompareToData.total_duration_per_demo_zone,
                        percentage_by_duration_range_in_demo_zone: fullCompareToData.percentage_by_duration_range_in_demo_zone,
                        demo_zone_visitors_counts: fullCompareToData.demo_zone_visitors_counts,
                        demo_zone_clear_lingerers_counts: fullCompareToData.demo_zone_clear_lingerers_counts,
                        demo_hotcold_by_visitors: fullCompareToData.demo_hotcold_by_visitors,
                        demo_hotcold_by_clear: fullCompareToData.demo_hotcold_by_clear,
                        demo_zone_funnels: fullCompareToData.demo_zone_funnels,

                        best_display_zone_by_visitors: fullCompareToData.best_display_zone_by_visitors,
                        best_display_zone_by_avg_duration: fullCompareToData.best_display_zone_by_avg_duration,
                        worst_display_zone_by_visitors: fullCompareToData.worst_display_zone_by_visitors,
                        total_duration_per_display_zone: fullCompareToData.total_duration_per_display_zone,
                        percentage_by_duration_range_in_display_zone: fullCompareToData.percentage_by_duration_range_in_display_zone,
                        display_zone_visitors_counts: fullCompareToData.display_zone_visitors_counts,
                        display_zone_clear_lingerers_counts: fullCompareToData.display_zone_clear_lingerers_counts,
                        display_hotcold_by_visitors: fullCompareToData.display_hotcold_by_visitors,
                        display_hotcold_by_clear: fullCompareToData.display_hotcold_by_clear,
                        display_zone_funnels: fullCompareToData.display_zone_funnels,

                        average_waiting_time: fullCompareToData.average_waiting_time,
                        average_time_in_zone_waiting: fullCompareToData.average_time_in_zone_waiting,
                        max_waiting_duration: fullCompareToData.max_waiting_duration,
                        max_waiting_zone: fullCompareToData.max_waiting_zone,
                        most_typical_waiting_time: fullCompareToData.most_typical_waiting_time,
                        real_wait_rate: fullCompareToData.real_wait_rate,
                        real_wait_count: fullCompareToData.real_wait_count,
                        average_interaction_time: fullCompareToData.average_interaction_time,
                        average_time_in_zone_interaction: fullCompareToData.average_time_in_zone_interaction,
                        max_interaction_duration: fullCompareToData.max_interaction_duration,
                        max_interaction_zone: fullCompareToData.max_interaction_zone,
                        most_typical_interaction_time: fullCompareToData.most_typical_interaction_time,
                        real_transaction_rate: fullCompareToData.real_transaction_rate,
                        real_transaction_count: fullCompareToData.real_transaction_count,
                        fastest_checkout_zone: fullCompareToData.fastest_checkout_zone,
                        slowest_checkout_zone: fullCompareToData.slowest_checkout_zone,
                        fastest_interaction_zone: fullCompareToData.fastest_interaction_zone,
                        slowest_interaction_zone: fullCompareToData.slowest_interaction_zone,
                        most_used_interaction_zone: fullCompareToData.most_used_interaction_zone,
                        least_used_interaction_zone: fullCompareToData.least_used_interaction_zone,
                        
                        total_opening_hours: fullCompareToData.total_opening_hours,
                        date: fullCompareToData.last_updated_date, // Add today's date to track when the data was fetched

                        checkout_interaction_usage_pct: type5Cmp?.usage_percentage ?? null,
                        checkout_interaction_total_visitors: type5Cmp?.total_visitors ?? null,
                        checkout_interaction_total_time_seconds: type5Cmp?.total_time_in_zone_seconds ?? null,
                        checkout_interaction_total_opening_hours: type5Cmp?.total_opening_hours ?? null,

                        checkout_cashier_usage_pct: type999Cmp?.usage_percentage ?? null,
                        checkout_cashier_total_visitors: type999Cmp?.total_visitors ?? null,
                        checkout_cashier_total_time_seconds: type999Cmp?.total_time_in_zone_seconds ?? null,
                        checkout_cashier_total_opening_hours: type999Cmp?.total_opening_hours ?? null,
                    }

                    fixedWidgetCompareToData.value = { ...newStoredCompareToData }
                    // console.log(fixedWidgetCompareToData.value)

                } catch (error) {
                    console.error('Error fetching fixed widget compare to... data:', error)
                }
            }
        }

        const handleUpdateDuration = async (newDuration) => {
            selectedDurationFixedWidgetData.value = newDuration
            await fetchFixedWidgetData()
        }

        const handleUpdateOpeningHoursMode = async (newMode) => {
            selectedOpeningHoursModeFixedWidgetData.value = newMode
            await fetchFixedWidgetData()
        }



        const changeDuration = (newDuration) => {
            duration.value = newDuration
        }
        
        const changeDateRangeMode = (newMode) => {
            dateRangeMode.value = newMode
        }

        const updateClearDatePicker = (newValue) => {
            clearDatePicker.value = newValue
        }

        const updateDataGranularity = (granularity) => {
            dataGranularity.value = granularity
        }

        onUnmounted(() => {
            controller.abort()
        })

        return {
            CompanyStore,
            changeDuration,
            changeDateRangeMode,
            dateRangeMode,
            updateClearDatePicker,
            clearDatePicker,
            updateDataGranularity,
            dataGranularity,
            handleUpdateDuration,
            handleUpdateOpeningHoursMode,
            fixedWidgetData,
            fixedWidgetCompareToData,
            fixedWidgetData30d,
            isLoading,

        }
    }
})
</script>
  
<style scoped>
.loader {
    width: 80px;
    padding: 8px;
    aspect-ratio: 1;
    border-radius: 50%;
    background: #B2C149;
    --_m: 
        conic-gradient(#0000 10%,#000),
        linear-gradient(#000 0 0) content-box;
    -webkit-mask: var(--_m);
            mask: var(--_m);
    -webkit-mask-composite: source-out;
            mask-composite: subtract;
    animation: spinner 1s infinite linear;
}
@keyframes spinner {to{transform: rotate(1turn)}}

</style>